// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * THE MODAL DISMISSAL CONTRACT.
 *
 * A user who opens an overlay must ALWAYS be able to get out of it. There is no
 * such thing as an overlay with one way out, so every overlay this module
 * creates ships all four exits, and they are not alternatives:
 *
 *   1. a close control inside the panel, >= 44x44 CSS px of hit area
 *   2. a pointerdown anywhere outside the panel
 *   3. Escape, from anywhere on the page
 *   4. a real touch sequence on the backdrop, not just a synthetic click
 *
 * `pointerdown` alone, never `click` AND `touchstart` on the same control: two
 * listeners on one control double-fire on a real tap, so the overlay closes and
 * instantly reopens — which a user experiences as a dead close button, and which
 * a scripted `.click()` in a test cannot see.
 *
 * The backdrop is a real hit area (`position: fixed; inset: 0`) below the panel
 * and above the page, and it is never `pointer-events: none`. The global
 * `[hidden] { display: none !important }` rule in main.css is what makes the
 * `hidden` attribute authoritative over the `display: flex` these panels carry —
 * without it the overlay stays painted whatever this code sets, and swallows
 * every tap on a phone.
 */

export interface Modal {
  open(): void;
  close(): void;
  readonly isOpen: boolean;
  readonly root: HTMLElement;
}

let openModal: Modal | null = null;

/** One document-level Escape listener rather than one per overlay. */
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && openModal) {
    event.preventDefault();
    openModal.close();
  }
});

export function createModal(id: string, title: string, bodyHtml: string): Modal {
  const root = document.createElement('div');
  root.className = 'modal';
  root.id = id;
  root.hidden = true;
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', title);
  root.innerHTML = `
    <div class="modal-backdrop" data-close="1"></div>
    <div class="modal-panel" role="document">
      <div class="modal-head">
        <h2>${title}</h2>
        <button type="button" class="icon-button" data-close="1" aria-label="Close ${title}">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
      </div>
      <div class="modal-body">${bodyHtml}</div>
    </div>`;

  const panel = root.querySelector('.modal-panel') as HTMLElement;
  let lastFocus: HTMLElement | null = null;

  const modal: Modal = {
    get isOpen() {
      return !root.hidden;
    },
    get root() {
      return root;
    },
    open() {
      if (openModal && openModal !== modal) openModal.close();
      lastFocus = document.activeElement as HTMLElement | null;
      root.hidden = false;
      openModal = modal;
      document.body.classList.add('overlay-open');
      (root.querySelector('.icon-button') as HTMLElement | null)?.focus();
    },
    close() {
      if (root.hidden) return;
      root.hidden = true;
      if (openModal === modal) openModal = null;
      document.body.classList.remove('overlay-open');
      lastFocus?.focus?.();
    },
  };

  // Exits 1, 2 and 4 in one handler. `pointerdown` covers mouse, touch and pen,
  // and fires where a synthetic click would not.
  root.addEventListener('pointerdown', (event) => {
    const target = event.target as HTMLElement;
    if (target.closest('[data-close]') || !panel.contains(target)) {
      event.preventDefault();
      modal.close();
    }
  });

  // Keyboard activation of the close button, which pointerdown does not cover.
  root.addEventListener('keydown', (event) => {
    if ((event.key === 'Enter' || event.key === ' ') && (event.target as HTMLElement).closest('[data-close]')) {
      event.preventDefault();
      modal.close();
    }
  });

  document.body.appendChild(root);
  return modal;
}
